// Web anti-piracy layer (blueprint, layers 4–6). These are deterrents: they stop casual
// copying in the browser. Real protection comes from encrypted/DRM streams, short-lived
// tokens and the viewer watermark, which the player and backend add on top.
import { useEffect, useSyncExternalStore } from 'react';
import { toast } from './store';

export type BlockReason = 'hidden' | 'blur' | 'devtools' | 'shield' | null;

let reason: BlockReason = null;
const listeners = new Set<() => void>();
function setReason(r: BlockReason) {
  if (r === reason) return;
  reason = r;
  listeners.forEach((l) => l());
}

/** Why playback must pause right now (tab hidden, window lost focus, dev tools, screenshot key). */
export function usePlaybackBlock(): BlockReason {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => listeners.delete(l); },
    () => reason,
    () => null
  );
}

const isEditable = (el: EventTarget | null) =>
  el instanceof HTMLElement && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

let lastRightClickToast = 0;
let shieldTimer: number | undefined;
let installed = false;

function shield(ms = 1500) {
  setReason('shield');
  document.documentElement.classList.add('kn-shield');
  window.clearTimeout(shieldTimer);
  shieldTimer = window.setTimeout(() => {
    document.documentElement.classList.remove('kn-shield');
    if (reason === 'shield') setReason(null);
  }, ms);
}

export function installGuards(): void {
  if (installed) return;
  installed = true;
  const d = document;

  d.addEventListener('contextmenu', (e) => {
    if (isEditable(e.target)) return;
    e.preventDefault();
    if (Date.now() - lastRightClickToast > 2500) {
      lastRightClickToast = Date.now();
      toast('Right-click is turned off on Kuch Naya');
    }
  });
  d.addEventListener('copy', (e) => { if (!isEditable(e.target)) e.preventDefault(); });
  d.addEventListener('cut', (e) => { if (!isEditable(e.target)) e.preventDefault(); });
  d.addEventListener('dragstart', (e) => e.preventDefault());
  d.addEventListener('selectstart', (e) => { if (!isEditable(e.target)) e.preventDefault(); });

  d.addEventListener('keydown', (e) => {
    const k = e.key.toLowerCase();
    const mod = e.ctrlKey || e.metaKey;
    const blocked =
      k === 'f12' ||
      (mod && ['s', 'u', 'p'].includes(k)) ||
      (mod && e.shiftKey && ['i', 'j', 'c', 's'].includes(k)) ||
      (e.metaKey && e.altKey && ['i', 'j', 'c'].includes(k));
    if (blocked) {
      e.preventDefault();
      e.stopPropagation();
      toast('That shortcut is turned off on Kuch Naya');
    }
    if (k === 'printscreen') shield();
  }, true);
  d.addEventListener('keyup', (e) => {
    if (e.key === 'PrintScreen') {
      shield();
      navigator.clipboard?.writeText('').catch(() => {});
    }
  }, true);

  window.addEventListener('beforeprint', () => shield(4000));
  d.addEventListener('visibilitychange', () => {
    if (d.hidden) setReason('hidden');
    else if (reason === 'hidden') setReason(null);
  });
  window.addEventListener('blur', () => { if (reason === null) setReason('blur'); });
  window.addEventListener('focus', () => { if (reason === 'blur' || reason === 'hidden') setReason(null); });

  // Dev-tools check only runs when the app is the top window (an embedded preview would
  // always look "docked" and trigger it falsely).
  let topLevel = false;
  try { topLevel = window.self === window.top; } catch { topLevel = false; }
  if (topLevel) {
    window.setInterval(() => {
      const open = window.outerWidth - window.innerWidth > 170 || window.outerHeight - window.innerHeight > 200;
      if (open && reason === null) setReason('devtools');
      else if (!open && reason === 'devtools') setReason(null);
    }, 1200);
  }
}

/** Lock browser zoom while a protected video page is open (Watch, Reels). */
export function useZoomLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const meta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    const original = meta?.content;
    if (meta && original && !/user-scalable=no/.test(original)) {
      meta.content = original + ', maximum-scale=1, user-scalable=no';
    }
    const onWheel = (e: WheelEvent) => { if (e.ctrlKey) e.preventDefault(); };
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && ['+', '-', '=', '0', '_'].includes(e.key)) e.preventDefault();
    };
    const onGesture = (e: Event) => e.preventDefault();
    const onTouch = (e: TouchEvent) => { if (e.touches.length > 1) e.preventDefault(); };
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKey, true);
    document.addEventListener('gesturestart', onGesture as EventListener, { passive: false } as AddEventListenerOptions);
    document.addEventListener('gesturechange', onGesture as EventListener, { passive: false } as AddEventListenerOptions);
    document.addEventListener('touchmove', onTouch, { passive: false });
    return () => {
      if (meta && original) meta.content = original;
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('keydown', onKey, true);
      document.removeEventListener('gesturestart', onGesture as EventListener);
      document.removeEventListener('gesturechange', onGesture as EventListener);
      document.removeEventListener('touchmove', onTouch);
    };
  }, [active]);
}

export const BLOCK_TEXT: Record<Exclude<BlockReason, null>, string> = {
  hidden: 'Video paused. Come back to this tab to keep watching.',
  blur: 'Video paused. Click here to keep watching.',
  devtools: 'Video paused while developer tools are open.',
  shield: 'Screenshots are not allowed on Kuch Naya videos.',
};
